import { useMemo } from "react";
import { useDashboardData } from "../../../context/DashboardContext";
import { DataTable, type ColumnDef } from "../../ui/data-table";
import type { WidgetComponentProps } from "../types";
import { Input } from "../../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);

interface GenericRow {
  [key: string]: unknown;
  id?: string;
  _id?: string;
}

interface RecentCallRow extends GenericRow {
  id: string;
  _id?: string;
  timestamp?: string;
  dateCallWasTaken?: string;
  leadName?: string;
  closer?: string;
  setter?: string;
  callOutcome?: string;
  cashCollected?: number;
  leadSourcePlatform?: string;
  leadSourceContent?: string;
}

/** Keys to exclude from auto-generated columns (internal/metadata fields) */
const AUTO_COLUMN_EXCLUDE = new Set(["_id", "__v", "createdAt", "updatedAt"]);

/** Convert camelCase or snake_case key into a readable header label */
function keyToHeader(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, "$1 $2") // camelCase -> camel Case
    .replace(/_/g, " ") // snake_case -> snake case
    .replace(/\b\w/g, (c) => c.toUpperCase()); // capitalize each word
}

/** Guess the format of a column based on key name and sample value */
function guessFormat(key: string, sampleValue: unknown): string | undefined {
  const lowerKey = key.toLowerCase();
  if (
    typeof sampleValue === "number" &&
    (lowerKey.includes("cash") || lowerKey.includes("revenue") || lowerKey.includes("price") || lowerKey.includes("cost") || lowerKey.includes("amount"))
  ) {
    return "currency";
  }
  if (
    typeof sampleValue === "string" &&
    (lowerKey === "timestamp" || lowerKey.includes("date") || lowerKey.includes("createdat") || lowerKey.includes("updatedat"))
  ) {
    return "date";
  }
  return undefined;
}

interface ColumnConfig {
  id: string;
  header: string;
  accessorKey?: string;
  format?: string;
  className?: string;
  headerClassName?: string;
}

interface TableFilterConfig {
  key: string;
  label: string;
}

function getRecentCallOutcomeClass(outcome: string): string {
  const normalized = outcome.toLowerCase().trim();

  if (
    normalized.includes("closed") ||
    normalized.includes("won") ||
    normalized.includes("taken - not closed")
  ) {
    return "bg-green-500/20 text-green-300";
  }

  if (
    normalized.includes("mrr") ||
    normalized.includes("deposit") ||
    normalized.includes("cancel") ||
    normalized.includes("pending")
  ) {
    return "bg-amber-500/20 text-amber-300";
  }

  if (
    normalized.includes("no show") ||
    normalized.includes("no-show") ||
    normalized.includes("not interested") ||
    normalized.includes("lost")
  ) {
    return "bg-red-500/20 text-red-300";
  }

  return "bg-muted text-muted-foreground";
}

export default function TableWidget({ config, title }: WidgetComponentProps) {
  const { page, tableData, rawData } = useDashboardData();

  const dataKey = config.dataKey as string;
  const columnsConfig = config.columns as ColumnConfig[] | undefined;

  const data = (tableData[dataKey] as GenericRow[] | undefined) || [];
  const recentCallsData = data as RecentCallRow[];
  const isRecentCallsTable =
    dataKey === "recentCalls" && (page === "dashboard" || page === "acquisition");

  const recentCallsColumns: ColumnDef<RecentCallRow>[] = useMemo(
    () => [
      {
        id: "timestamp",
        header: "Timestamp",
        headerClassName: "w-[130px]",
        className: "w-[130px] align-top",
        cell: (row) => {
          if (!row.timestamp) return <span className="text-muted-foreground">-</span>;
          const date = new Date(row.timestamp);
          if (Number.isNaN(date.getTime())) {
            return <span className="text-muted-foreground">{row.timestamp}</span>;
          }
          return (
            <div className="text-muted-foreground leading-tight whitespace-nowrap">
              <div>{date.toLocaleDateString()}</div>
              <div>{date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
            </div>
          );
        },
      },
      {
        id: "dateCallWasTaken",
        header: "Date Call Was Taken",
        headerClassName: "w-[150px]",
        className: "w-[150px] whitespace-nowrap align-top",
        cell: (row) =>
          row.dateCallWasTaken ? (
            <span className="text-muted-foreground">{row.dateCallWasTaken}</span>
          ) : (
            <span className="text-muted-foreground">-</span>
          ),
      },
      {
        id: "leadName",
        header: "Lead Name",
        accessorKey: "leadName",
        headerClassName: "w-[170px]",
        className: "w-[170px] font-medium text-foreground align-top",
      },
      {
        id: "closer",
        header: "Closer",
        accessorKey: "closer",
        headerClassName: "w-[150px]",
        className: "w-[150px] align-top",
      },
      {
        id: "setter",
        header: "Setter",
        accessorKey: "setter",
        headerClassName: "w-[150px]",
        className: "w-[150px] align-top",
      },
      {
        id: "source",
        header: "Source",
        headerClassName: "w-[260px]",
        className: "w-[260px] align-top",
        cell: (row) => {
          const platform = row.leadSourcePlatform?.trim() ?? "";
          const content = row.leadSourceContent?.trim() ?? "";
          if (!platform && !content) return <span className="text-muted-foreground">-</span>;
          const sourceLabel = `${platform}${platform && content ? " | " : ""}${content}`;
          return (
            <span
              className="block overflow-hidden text-ellipsis whitespace-nowrap text-muted-foreground"
              title={sourceLabel}
            >
              {sourceLabel}
            </span>
          );
        },
      },
      {
        id: "callOutcome",
        header: "Call Outcome",
        headerClassName: "w-[170px]",
        className: "w-[170px] whitespace-nowrap align-top",
        cell: (row) => {
          const outcome = row.callOutcome?.trim();
          if (!outcome) return <span className="text-muted-foreground">-</span>;
          return (
            <span className={`inline-flex rounded-md px-2.5 py-1 text-xs font-medium ${getRecentCallOutcomeClass(outcome)}`}>
              {outcome}
            </span>
          );
        },
      },
      {
        id: "cashCollected",
        header: "Cash Collected",
        headerClassName: "w-[150px] text-right",
        className: "w-[150px] whitespace-nowrap text-right align-top",
        cell: (row) =>
          typeof row.cashCollected === "number" && row.cashCollected > 0 ? (
            <span className="font-medium text-green-500">{formatCurrency(row.cashCollected)}</span>
          ) : (
            <span className="text-muted-foreground">-</span>
          ),
      },
    ],
    [],
  );

  // Build columns from config, or auto-generate from data keys
  const columns: ColumnDef<GenericRow>[] = useMemo(() => {
    if (isRecentCallsTable) {
      return recentCallsColumns as ColumnDef<GenericRow>[];
    }

    // If explicit columns are configured, use them
    const effectiveConfig: ColumnConfig[] | undefined = columnsConfig && columnsConfig.length > 0
      ? columnsConfig
      : undefined;

    // Auto-generate columns from the first row's keys when no config is provided
    if (!effectiveConfig) {
      const sampleRow = data[0];
      if (!sampleRow) return [];
      const keys = Object.keys(sampleRow).filter((k) => !AUTO_COLUMN_EXCLUDE.has(k));
      return keys.map((key) => {
        const format = guessFormat(key, sampleRow[key]);
        return {
          id: key,
          header: keyToHeader(key),
          accessorKey: key,
          className: format === "currency" ? "text-right" : undefined,
          headerClassName: format === "currency" ? "text-right" : undefined,
          cell: (row: GenericRow) => {
            const value = row[key];
            if (format === "currency" && typeof value === "number") {
              return value > 0 ? (
                <span className="text-green-600 font-medium">{formatCurrency(value)}</span>
              ) : (
                <span className="text-muted-foreground">-</span>
              );
            }
            if (format === "date" && value) {
              const date = new Date(value as string);
              return <span className="text-muted-foreground text-xs">{date.toLocaleDateString()}</span>;
            }
            if (value === null || value === undefined || value === "") {
              return <span className="text-muted-foreground">-</span>;
            }
            // Skip arrays and nested objects (e.g., cashByOffer)
            if (Array.isArray(value) || (typeof value === "object" && value !== null)) {
              return <span className="text-muted-foreground">-</span>;
            }
            return <span>{String(value)}</span>;
          },
        };
      });
    }

    return effectiveConfig.map((col) => ({
      id: col.id,
      header: col.header,
      accessorKey: col.accessorKey,
      className: col.className,
      headerClassName: col.headerClassName,
      cell: (row: GenericRow) => {
        const value = col.accessorKey ? row[col.accessorKey] : row[col.id];
        if (col.format === "currency" && typeof value === "number") {
          return value > 0 ? (
            <span className="text-green-600 font-medium">{formatCurrency(value)}</span>
          ) : (
            <span className="text-muted-foreground">-</span>
          );
        }
        if (col.format === "date" && value) {
          const date = new Date(value as string);
          return <span className="text-muted-foreground text-xs">{date.toLocaleDateString()}</span>;
        }
        if (value === null || value === undefined || value === "") {
          return <span className="text-muted-foreground">-</span>;
        }
        // Skip arrays and nested objects
        if (Array.isArray(value) || (typeof value === "object" && value !== null)) {
          return <span className="text-muted-foreground">-</span>;
        }
        return <span>{String(value)}</span>;
      },
    }));
  }, [columnsConfig, data, isRecentCallsTable, recentCallsColumns]);

  // Support server-side pagination from rawData
  const pagination = rawData.pagination as {
    totalItems: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
  } | undefined;

  const tableLoading = rawData.tableLoading as boolean | undefined;
  const tableFilters = (rawData.tableFilters as Record<string, Record<string, string>> | undefined) || {};
  const currentTableFilters = tableFilters[dataKey] || {};
  const setTableFilter = rawData.setTableFilter as ((tableKey: string, field: string, value: string) => void) | undefined;
  const clearTableFilters = rawData.clearTableFilters as ((tableKey: string) => void) | undefined;
  const searchConfig = (config.search as { enabled?: boolean; placeholder?: string; keys?: string[] } | undefined) || undefined;
  const filterConfigs = ((config.filters as TableFilterConfig[] | undefined) || []).filter(Boolean);

  // CRUD handlers from rawData (for pages like Acquisition with editable tables)
  const handlePageChange = rawData.handlePageChange as ((page: number) => void) | undefined;
  const handlePageSizeChange = rawData.handlePageSizeChange as ((size: number) => void) | undefined;
  const handleEdit = rawData.handleEditCall as ((row: GenericRow) => void) | undefined;
  const handleDelete = rawData.handleDeleteCall as ((row: GenericRow) => void) | undefined;

  const filterOptionsMap = useMemo(() => {
    const statusOptions = [
      { value: "closed", label: "Closed" },
      { value: "pending", label: "Pending" },
      { value: "no-show", label: "No Show" },
      { value: "not-interested", label: "Not Interested" },
    ];

    const dynamicOptions = (key: keyof RecentCallRow) =>
      Array.from(new Set(recentCallsData.map((row) => String(row[key] || "").trim()).filter(Boolean)))
        .sort((a, b) => a.localeCompare(b))
        .map((value) => ({ value, label: value }));

    return {
      status: statusOptions,
      closer: dynamicOptions("closer"),
      setter: dynamicOptions("setter"),
      callOutcome: dynamicOptions("callOutcome"),
    } as Record<string, Array<{ value: string; label: string }>>;
  }, [recentCallsData]);

  const filteredClientData = useMemo(() => {
    if (isRecentCallsTable) return recentCallsData;
    let rows = [...data];
    const searchValue = currentTableFilters.search?.trim().toLowerCase();
    if (searchValue && searchConfig?.keys?.length) {
      rows = rows.filter((row) =>
        searchConfig.keys!.some((key) => String(row[key] || "").toLowerCase().includes(searchValue)),
      );
    }
    for (const filter of filterConfigs) {
      const value = currentTableFilters[filter.key];
      if (!value) continue;
      rows = rows.filter((row) => String(row[filter.key] || "") === value);
    }
    return rows;
  }, [currentTableFilters, data, filterConfigs, isRecentCallsTable, recentCallsData, searchConfig]);

  // Build server pagination props if pagination data exists
  const serverPaginationProps = pagination && handlePageChange && handlePageSizeChange
    ? {
        currentPage: pagination.currentPage,
        pageSize: pagination.pageSize,
        totalItems: pagination.totalItems,
        totalPages: pagination.totalPages,
        pageSizeOptions: [10, 25, 50, 100],
        onPageChange: handlePageChange,
        onPageSizeChange: handlePageSizeChange,
      }
    : undefined;

  return (
    <div className="h-full flex flex-col">
      <div className="mb-4 pt-0.5">
        <h3 className="overflow-visible leading-tight text-lg font-semibold">{title}</h3>
        {isRecentCallsTable && (
          <p className="mt-1 text-sm text-muted-foreground">Latest call activity</p>
        )}
      </div>
      {(searchConfig?.enabled || filterConfigs.length > 0) && (
        <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center">
          {searchConfig?.enabled && setTableFilter && (
            <Input
              value={currentTableFilters.search || ""}
              onChange={(event) => setTableFilter(dataKey, "search", event.target.value)}
              placeholder={searchConfig.placeholder || "Search..."}
              className="h-9 lg:max-w-sm"
            />
          )}
          {filterConfigs.length > 0 && setTableFilter && (
            <div className="flex flex-1 flex-wrap gap-2">
              {filterConfigs.map((filter) => (
                <Select
                  key={filter.key}
                  value={currentTableFilters[filter.key] || "__all__"}
                  onValueChange={(value) => setTableFilter(dataKey, filter.key, value === "__all__" ? "" : value)}
                >
                  <SelectTrigger className="h-9 min-w-[150px]">
                    <SelectValue placeholder={filter.label} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All {filter.label}</SelectItem>
                    {(filterOptionsMap[filter.key] || []).map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ))}
              {clearTableFilters && (
                <button
                  type="button"
                  onClick={() => clearTableFilters(dataKey)}
                  className="h-9 rounded-md border border-border px-3 text-sm text-muted-foreground transition-colors hover:bg-muted"
                >
                  Clear
                </button>
              )}
            </div>
          )}
        </div>
      )}
      <div className="flex-1 min-h-0">
        <DataTable
          data={isRecentCallsTable ? recentCallsData : filteredClientData}
          columns={columns}
          rowKey={(config.rowKey as string) || (isRecentCallsTable ? "_id" : "id")}
          emptyMessage={(config.emptyMessage as string) || "No data found."}
          loading={tableLoading}
          serverPagination={serverPaginationProps}
          onEdit={handleEdit}
          onDelete={handleDelete}
          className={isRecentCallsTable ? "h-full border-border/60 bg-card/95" : undefined}
          tableClassName={isRecentCallsTable ? "min-w-[1480px] table-fixed" : undefined}
        />
      </div>
    </div>
  );
}
